import logger from '@/utils/client-logger';
import { captureReportingAnalytics, isBrowserAnalytics } from './reliability/browserAnalytics';
import { getEventAdapter } from './adapters/eventAdapters';
import { ProviderRegistry } from './providerRegistry';
import { Provider, ProviderType, TrackingEvent } from './types';
import { generateEventId } from './utils/eventIdGenerator';
import {
  captureSnowplowAtomicPageProperties,
  readSnowplowAtomicPageProperties,
  registerSnowplowAtomicPageProperties,
  type SnowplowAtomicPageProperties,
} from './utils/snowplowAtomicPageProperties';
import { cloneTrackingValue } from './utils/trackingEventSnapshot';

export class ReportingCenter {
  private static instance: ReportingCenter;

  static getInstance(): ReportingCenter {
    if (!ReportingCenter.instance) {
      ReportingCenter.instance = new ReportingCenter();
      // 暴露日志控制方法到window对象
      if (typeof window !== 'undefined') {
        (window as any).enableReportCenterLog = ReportingCenter.instance.enableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).disableReportCenterLog = ReportingCenter.instance.disableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).getReportCenterLogStatus = ReportingCenter.instance.getLoggingStatus.bind(
          ReportingCenter.instance,
        );
        (window as any).listReportCenterProviders = ReportingCenter.instance.listProviders.bind(
          ReportingCenter.instance,
        );
      }
    }
    return ReportingCenter.instance;
  }

  private registry: ProviderRegistry;
  private initialized = false;
  private eventQueue: Array<{
    event: TrackingEvent | undefined;
    providers?: ProviderType[];
    adaptionData?: unknown;
    adapterType?: string;
    /** 事件进入初始化队列时捕获的 Snowplow 根级页面字段。 */
    snowplowAtomicPageProperties: SnowplowAtomicPageProperties | null;
  }> = [];

  private constructor() {
    this.registry = new ProviderRegistry();
  }

  /** 两浏览器渠道由独立编排器初始化，其他Provider维持既有等待合同。 */
  async initialize(): Promise<void> {
    if (typeof window === 'undefined' || typeof document === 'undefined' || this.initialized)
      return;

    const providers = this.registry
      .getEnabledProviders()
      .filter(
        (provider) =>
          !isBrowserAnalytics() ||
          (provider.type !== ProviderType.SENSORS && provider.type !== ProviderType.SNOWPLOW),
      );
    const initPromises = providers.map((provider) => provider.init());

    await Promise.allSettled(initPromises);

    // Process any events that were queued during initialization
    this.initialized = true;
    this.processQueue();
  }

  registerProvider(provider: Provider): void {
    this.registry.registerProvider(provider);
  }

  /**
   * 追踪事件
   * @param event 事件数据 (可以是原始 TrackingEvent 或业务数据)
   * @param specificProviders 指定的 Provider 类型数组
   * @param adapterType 适配器类型，用于触发对应适配器 (如 'checkout.initiate')
   */
  track(
    event: TrackingEvent,
    specificProviders?: ProviderType[],
    adaptionData?: unknown,
    adapterType?: string,
  ): void {
    // SSR不进入文档全局队列，避免跨请求保留事件。
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    // 适配器模式允许省略原始事件，登录成功等调用只提供 adaptionData。
    const capturedEvent =
      adapterType && isBrowserAnalytics()
        ? { ...event, eventId: event?.eventId ?? generateEventId() }
        : event;
    if (isBrowserAnalytics())
      captureReportingAnalytics(capturedEvent, specificProviders, adaptionData, adapterType);
    this.trackWithAtomicPageProperties(capturedEvent, specificProviders, adaptionData, adapterType);
  }

  /**
   * 使用调用时页面快照派发事件；队列 flush 可显式复用入队快照，避免重新读取当前页面。
   */
  private trackWithAtomicPageProperties(
    event: TrackingEvent | undefined,
    specificProviders?: ProviderType[],
    adaptionData?: unknown,
    adapterType?: string,
    queuedSnowplowAtomicPageProperties?: SnowplowAtomicPageProperties | null,
  ): void {
    if (!event && !adapterType) {
      logger.error('❌ ReportingCenter requires an event when adapterType is not provided');
      return;
    }

    const snowplowAtomicPageProperties =
      queuedSnowplowAtomicPageProperties !== undefined
        ? queuedSnowplowAtomicPageProperties
        : !adapterType && event?.eventName === 'page_view'
          ? null
          : ((event ? readSnowplowAtomicPageProperties(event) : null) ??
            captureSnowplowAtomicPageProperties());

    if (
      isBrowserAnalytics() &&
      specificProviders?.length &&
      specificProviders.every(
        (type) => type === ProviderType.SENSORS || type === ProviderType.SNOWPLOW,
      )
    )
      return;
    // Queue events if system isn't initialized yet
    if (!this.initialized) {
      const queuedEvent = cloneTrackingValue(event);
      if (queuedEvent && snowplowAtomicPageProperties) {
        registerSnowplowAtomicPageProperties(queuedEvent, snowplowAtomicPageProperties);
      }
      this.eventQueue.push({
        event: queuedEvent,
        providers: cloneTrackingValue(specificProviders),
        adaptionData: cloneTrackingValue(adaptionData),
        adapterType,
        snowplowAtomicPageProperties: snowplowAtomicPageProperties
          ? { ...snowplowAtomicPageProperties }
          : null,
      });
      return;
    }

    const providers = this.resolveTargetProviders(specificProviders).filter(
      (provider) =>
        !isBrowserAnalytics() ||
        (provider.type !== ProviderType.SENSORS && provider.type !== ProviderType.SNOWPLOW),
    );

    // 如果有 adapterType，必须使用适配器
    if (adapterType) {
      const adapter = getEventAdapter(adapterType);
      if (!adapter) {
        logger.error(`❌ No adapter found for adapter type: ${adapterType}`);
        return;
      }

      // 生成统一的事件 ID
      const eventId = isBrowserAnalytics()
        ? (event?.eventId ?? generateEventId())
        : generateEventId();
      logger.log(`🎯 Using event adapter for type: ${adapterType}`, {
        originalData: event,
        targetProviders: providers.map((p) => p.type),
        eventId,
      });

      // 为每个 Provider 使用适配器生成适配后的事件
      providers.forEach((provider) => {
        if (!provider.isInitialized()) {
          logger.warn(`⚠️ Provider ${provider.type} is not initialized`);
          return;
        }

        const adaptedEvent = adapter(adaptionData, provider.type, eventId);
        if (!adaptedEvent) {
          logger.error(
            `❌ Adapter failed to generate event for provider ${provider.type} with adapter type: ${adapterType}`,
          );
          return;
        }

        logger.log(`📤 Sending adapted event to ${provider.type}:`, adaptedEvent);
        if (Array.isArray(adaptedEvent)) {
          adaptedEvent.forEach((evt) =>
            this.trackProviderEvent(provider, evt, snowplowAtomicPageProperties),
          );
        } else {
          this.trackProviderEvent(provider, adaptedEvent, snowplowAtomicPageProperties);
        }
      });
    } else {
      // 没有 adapterType，使用传统方式处理
      this.trackWithoutAdapter(event as TrackingEvent, providers, snowplowAtomicPageProperties);
    }
  }

  /**
   * 启用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时启用所有Provider的日志
   */
  enableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('enableLog' in provider && typeof provider.enableLog === 'function') {
        (provider as any).enableLog();
        logger.log(`✅ Enabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 禁用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时禁用所有Provider的日志
   */
  disableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('disableLog' in provider && typeof provider.disableLog === 'function') {
        (provider as any).disableLog();
        logger.log(`❌ Disabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 获取当前所有Provider的日志状态
   */
  getLoggingStatus(): Record<string, boolean | 'not_supported'> {
    const providers = this.registry.getAllProviders();
    const status: Record<string, boolean | 'not_supported'> = {};

    providers.forEach((provider) => {
      if ('loggingEnabled' in provider) {
        status[provider.type] = (provider as any).loggingEnabled;
      } else {
        status[provider.type] = 'not_supported';
      }
    });

    return status;
  }

  /**
   * 列出所有已注册的Provider
   */
  listProviders(): string[] {
    return this.registry.getAllProviders().map((provider) => provider.type);
  }

  /**
   * 获取特定类型的Provider实例
   */
  getProvider(type: ProviderType): Provider | undefined {
    return this.registry.getProvider(type);
  }

  /**
   * 不使用适配器的传统追踪方式
   */
  private trackWithoutAdapter(
    event: TrackingEvent,
    providers: Provider[],
    snowplowAtomicPageProperties: SnowplowAtomicPageProperties | null,
  ): void {
    const sensorsEvent = this.withSensorsEventId(event, providers);
    providers.forEach((provider) => {
      const eventForProvider =
        provider.type === ProviderType.SENSORS || provider.type === ProviderType.SNOWPLOW
          ? sensorsEvent
          : event;

      this.trackProviderEvent(provider, eventForProvider, snowplowAtomicPageProperties);
    });
  }

  /**
   * 当本次目标包含 Sensors 时，为神策及其 Snowplow 镜像事件补齐同一个 eventId。
   */
  private withSensorsEventId(event: TrackingEvent, providers: Provider[]): TrackingEvent {
    const hasSensors = providers.some((provider) => provider.type === ProviderType.SENSORS);
    if (!hasSensors || event.eventId) {
      return event;
    }

    return {
      ...event,
      eventId: generateEventId(),
    };
  }

  private resolveTargetProviders(specificProviders?: ProviderType[]): Provider[] {
    let providers = this.registry.getEnabledProviders();

    if (specificProviders && specificProviders.length > 0) {
      providers = providers.filter((provider) => specificProviders.includes(provider.type));
    }

    return this.withSensorsSnowplowMirror(providers);
  }

  /**
   * 当目标包含 Sensors 时，自动纳入 Snowplow，并保证调用顺序为 Sensors -> Snowplow。
   */
  private withSensorsSnowplowMirror(providers: Provider[]): Provider[] {
    const hasSensors = providers.some((provider) => provider.type === ProviderType.SENSORS);
    const hasSnowplow = providers.some((provider) => provider.type === ProviderType.SNOWPLOW);

    let resolvedProviders = providers;
    if (hasSensors && !hasSnowplow) {
      const snowplowProvider = this.registry.getProvider(ProviderType.SNOWPLOW);
      if (snowplowProvider?.isEnabled) {
        resolvedProviders = [...providers, snowplowProvider];
      }
    }

    return this.orderSensorsSnowplowFirst(resolvedProviders);
  }

  private orderSensorsSnowplowFirst(providers: Provider[]): Provider[] {
    const sensorsProvider = providers.find((provider) => provider.type === ProviderType.SENSORS);
    if (!sensorsProvider) {
      return providers;
    }

    const snowplowProvider = providers.find((provider) => provider.type === ProviderType.SNOWPLOW);
    const remainingProviders = providers.filter(
      (provider) =>
        provider.type !== ProviderType.SENSORS && provider.type !== ProviderType.SNOWPLOW,
    );

    if (!snowplowProvider) {
      return [sensorsProvider, ...remainingProviders];
    }

    return [sensorsProvider, snowplowProvider, ...remainingProviders];
  }

  /** 发送单个 Provider 事件，并只给 Snowplow 绑定不可枚举页面快照。 */
  private trackProviderEvent(
    provider: Provider,
    event: TrackingEvent,
    snowplowAtomicPageProperties: SnowplowAtomicPageProperties | null = null,
  ): void {
    if (!provider.isInitialized()) {
      logger.warn(`⚠️ Provider ${provider.type} is not initialized`);
      return;
    }

    try {
      if (provider.type === ProviderType.SNOWPLOW && snowplowAtomicPageProperties) {
        registerSnowplowAtomicPageProperties(event, snowplowAtomicPageProperties);
      }
      provider.track(event);
    } catch (error) {
      logger.error(`❌ Provider ${provider.type} tracking failed:`, error);
    }
  }

  private processQueue(): void {
    if (this.eventQueue.length === 0) return;

    // 先清空原队列再逐条处理，避免发送期间重入导致同一事件被重复 flush。
    const queuedEvents = [...this.eventQueue];
    this.eventQueue = [];
    queuedEvents.forEach(
      ({ event, providers, adapterType, adaptionData, snowplowAtomicPageProperties }) => {
        try {
          this.trackWithAtomicPageProperties(
            event,
            providers,
            adaptionData,
            adapterType,
            snowplowAtomicPageProperties,
          );
        } catch (error) {
          // 单条 adapter 或派发异常不能阻断后续队列事件，避免首屏埋点连带丢失。
          logger.error('❌ Failed to process queued reporting event:', error);
        }
      },
    );
  }

  /**
   * 获取目标Provider列表
   */
  private getTargetProviders(providerTypes?: string[]): Provider[] {
    const allProviders = this.registry.getAllProviders();

    // 如果没有指定类型或传入空数组，返回所有Provider
    if (!providerTypes || providerTypes.length === 0) {
      return allProviders;
    }

    // 过滤指定类型的Provider
    return allProviders.filter((provider) => providerTypes.includes(provider.type));
  }
}
